import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BuildYourX1 } from '../components/builder/BuildYourX1';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { PrescriptionSection } from '../components/PrescriptionSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { ObjectionTeaser } from '../components/ObjectionTeaser';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest, scrollToPackCta } from '../utils/offerBus';
import { Offer } from '../data/product';
import { BUILDER_ID } from '../data/builder';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const objection = useObjectionTrigger();
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);

  // Any generic "#offer" buy link lands on the $29.95 builder first; the packs sit right below it.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const link = (event.target as HTMLElement | null)?.closest('a');
      if (!link || link.getAttribute('href') !== '#offer') return;
      event.preventDefault();
      document.getElementById(BUILDER_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    trackCustom('objection_selected', {
      objection: id
    });
    objection.close();
    // "Just looking" is respected: no funnel, no further interruption this session.
    if (id === 'looking') {
      trackCustom('objection_just_looking_selected');
      return;
    }
    setActiveObjection(id);
    if (choice.recommend && choice.recommend !== 'business') setSelectedOffer(choice.recommend);
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." 5OFF only applies to the standard single, so preselect it.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    objection.close();
    setSelectedOffer('single');
    scrollToPackCta('single');
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1649" data-mp="2mc76p65lnvxp">
      {showAnnouncementBar && <AnnouncementBar data-id="element-1650" data-mp="2qbpldk6tvas3" />}
      <SiteNav data-id="element-1651" data-mp="2qchaqd9u0c9x" />
      <main className="pb-24 lg:pb-0" data-id="element-1652" data-mp="2qawodaxo7zn5">
        {/* 1. Promise + product */}
        <Hero data-id="element-1653" data-mp="2va0yq4wc03f2" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-1654" data-mp="3020v5nd70e6w" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-1655" data-mp="351drcypyexm5" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-1656" data-mp="3a0qd1g539yn5" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-1657" data-mp="39ync412eel3n" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-1658" data-mp="3e34ubvt40qcb" />
        {/* 6b. Prescription ready today, RX Insert in development */}
        <PrescriptionSection data-id="element-1659" data-mp="12g1f6tzuwdp9" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-1660" data-mp="ycv9pokkiqju" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-1661" data-mp="tea9hp5bg6ve" />
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-1662" data-mp="tgec83rhazu3" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-1663" data-mp="ogidok1jw0lx" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-1664" data-mp="1m1xnglc2zz92" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1665" data-mp="1h44l63dwfpe2" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1666" data-mp="1ccmgb48r94nt" />
        {/* 11. The decision: build your own from $29.95 first ... */}
        <BuildYourX1 data-id="element-1667" data-mp="17el1ivid3ri5" />
        {/* 11b. ... then, scrolling on, ready-made Complete Systems and multi-pack deals */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1668" data-mp="24uictssknm84" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} onChooseAnother={objection.reopen} data-id="element-1669" data-mp="3ubncuhsh4qra" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1670" data-mp="28mf7xdm7vsg" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1671" data-mp="3kjq199xjzws1" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1672" data-mp="3pib2ncojkmi2" />
      </main>
      <SiteFooter data-id="element-1673" data-mp="3amld54k5dr8l" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1674" data-mp="3am3hoa5b7h1q" />
      <ObjectionTeaser visible={objection.teaser} onExpand={objection.expand} onDismiss={objection.dismissTeaser} data-id="element-1675" data-mp="3alaaj82fupj2" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1676" data-mp="31mgd5fha9v4g" />
    </div>;
}